import { 
  doc, 
  runTransaction,
  Timestamp, 
  getDoc,
  collection,
  query,
  where,
  getDocs 
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { MoveOutRequest } from '../types/moveOutRequest';
import { matchesCurrentMoveOutRequest } from '../utils/portvilleMoveOutRequest';

export class MoveOutService {
  /**
   * 안전한 Timestamp 변환 함수
   */
  private static toTimestamp(dateValue: any): Timestamp {
    if (dateValue instanceof Timestamp) {
      return dateValue;
    }
    if (dateValue instanceof Date) {
      return Timestamp.fromDate(dateValue);
    }
    if (typeof dateValue === 'object' && dateValue.seconds) {
      // Firestore Timestamp 객체 형태
      return new Timestamp(dateValue.seconds, dateValue.nanoseconds || 0);
    }
    throw new Error('Invalid date value');
  }
  /**
   * 퇴거신청 상세 정보 조회
   */
  static async getMoveOutRequest(requestId: string): Promise<MoveOutRequest | null> {
    try {
      const docRef = doc(db, 'portville_move_out_requests', requestId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          appliedAt: data.appliedAt.toDate(),
          desiredMoveOutDate: data.desiredMoveOutDate.toDate(),
          processedAt: data.processedAt ? data.processedAt.toDate() : undefined
        } as MoveOutRequest;
      }
      
      return null;
    } catch (error) {
      console.error('퇴거신청 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 호실의 대기중인 퇴거신청 조회
   */
  static async getPendingMoveOutByRoomId(roomId: string): Promise<MoveOutRequest | null> {
    try {
      const room = (await getDoc(doc(db, 'portville_rooms', roomId))).data();
      if (!room?.currentResidencyId || !['occupied', 'contracting'].includes(room.status)) return null;
      const residency = (await getDoc(doc(db, 'portville_residencies', room.currentResidencyId))).data();
      if (!residency || !['active', 'contracting'].includes(residency.status)) return null;
      const q = query(
        collection(db, 'portville_move_out_requests'),
        where('roomId', '==', roomId),
        where('status', '==', 'pending')
      );
      
      const querySnapshot = await getDocs(q);
      
      const requestDoc = querySnapshot.docs
        .filter(snapshot => matchesCurrentMoveOutRequest(snapshot.data() as any, {
          roomId, residencyId: room.currentResidencyId, residentUid: residency.residentUid, createdAt: residency.createdAt
        }))
        .sort((a, b) => b.data().appliedAt.toMillis() - a.data().appliedAt.toMillis())[0];
      if (requestDoc) {
        const data = requestDoc.data();
        return {
          id: requestDoc.id,
          ...data,
          appliedAt: data.appliedAt.toDate(),
          desiredMoveOutDate: data.desiredMoveOutDate.toDate(),
          processedAt: data.processedAt ? data.processedAt.toDate() : undefined
        } as MoveOutRequest;
      }
      
      return null;
    } catch (error) {
      console.error('호실별 퇴거신청 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 퇴거신청 승인 (계약 종료일 변경 포함)
   */
  static async approveMoveOutRequest(
    requestId: string, 
    adminUid: string, 
    notes?: string
  ): Promise<void> {
    await runTransaction(db, async transaction => {
      const requestRef = doc(db, 'portville_move_out_requests', requestId);
      const requestSnap = await transaction.get(requestRef);
      const request = requestSnap.data();
      if (!request || request.status !== 'pending') {
        throw new Error('이미 처리되었거나 존재하지 않는 퇴거신청입니다.');
      }
      const roomSnap = await transaction.get(doc(db, 'portville_rooms', request.roomId));
      const room = roomSnap.data();
      const residencyId = room?.currentResidencyId;
      if (!residencyId) throw new Error('현재 입주 기록을 찾을 수 없습니다.');
      const residencyRef = doc(db, 'portville_residencies', residencyId);
      const residencySnap = await transaction.get(residencyRef);
      const residency = residencySnap.data();
      if (!residency || !['active', 'contracting'].includes(residency.status) ||
          !residency.contractSignedAt || !matchesCurrentMoveOutRequest(request as any, {
            roomId: request.roomId, residencyId, residentUid: residency.residentUid, createdAt: residency.createdAt
          }) || residency.roomId !== request.roomId) {
        throw new Error('현재 유효한 입주 기록에 해당하는 신청이 아닙니다.');
      }
      const now = Timestamp.now();
      if (residency.occupancyPeriod) {
        const newEndDate = this.toTimestamp(request.desiredMoveOutDate);
        transaction.update(residencyRef, {
          occupancyPeriod: { ...residency.occupancyPeriod, endDate: newEndDate },
          moveOutInfo: {
            originalEndDate: residency.occupancyPeriod.endDate,
            approvedMoveOutDate: newEndDate, moveOutReason: request.moveOutReason,
            approvedAt: now, approvedBy: adminUid, notes: notes || null
          },
          updatedAt: now
        });
      }
      transaction.update(requestRef, {
        status: 'approved', processedAt: now, processedBy: adminUid,
        notes: notes || null, residencyId, updatedAt: now
      });
    });
  }

  /**
   * 퇴거신청 반려
   */
  static async rejectMoveOutRequest(
    requestId: string, 
    adminUid: string, 
    notes: string
  ): Promise<void> {
    if (!notes.trim()) throw new Error('반려 사유를 입력해주세요.');
    await runTransaction(db, async transaction => {
      const requestRef = doc(db, 'portville_move_out_requests', requestId);
      const snapshot = await transaction.get(requestRef);
      if (!snapshot.exists() || snapshot.data().status !== 'pending') {
        throw new Error('이미 처리되었거나 존재하지 않는 퇴거신청입니다.');
      }
      const now = Timestamp.now();
      transaction.update(requestRef, {
        status: 'rejected', processedAt: now, processedBy: adminUid,
        notes: notes.trim(), updatedAt: now
      });
    });
  }

  static async terminateResidency(roomId: string, residencyId: string): Promise<void> {
    await httpsCallable(functions, 'terminatePortvilleResidency')({ roomId, residencyId });
  }
}
